import { computed, effect, inject, Injectable } from '@angular/core';
import { IndexedDBProviderService } from '../indexed-db/indexed-db.provider.service';
import { BookmarkDBRecord } from 'src/app/interfaces/bookmark-db-record';

@Injectable({
  providedIn: 'root'
})
export class BookmarksProviderService {
  private readonly STORAGE_KEY = 'bookmarks';
  private readonly dbVersion = 1;
  private indexedDBProvider = inject(IndexedDBProviderService);
  private openPromise?: Promise<any>;
  private dbState = computed(() => this.indexedDBProvider.status());

  constructor() {
    this.openDB();

    effect(async () => {
      if (this.dbState() === 'open') {
        const allDBItems = await this.indexedDBProvider.load<BookmarkDBRecord>(this.STORAGE_KEY);
        const dbItemsWithoutDates = allDBItems?.filter(d => !d.dateAdded) ?? [];
        for (let i = 0; i < dbItemsWithoutDates.length; i++) {
          const dbItem = dbItemsWithoutDates[i];
          await this.add(dbItem.sku, dbItem.imageData);
        }
      }
    });
  }

  private async openDB() {
    this.openPromise = this.indexedDBProvider.open(this.STORAGE_KEY, this.dbVersion, (db) => {
      db.createObjectStore(this.STORAGE_KEY);
    });
  }

  async remove(sku: string) {
    if (this.dbState() !== 'open') {
      await this.openDB();
    }

    if (this.openPromise) {
      await this.openPromise;
    }
    await this.indexedDBProvider.delete(this.STORAGE_KEY, sku);
  }

  async add(sku: string, imageData: string) {
    if (this.dbState() !== 'open') {
      await this.openDB();
    }

    if (this.openPromise) {
      await this.openPromise;
    }
    await this.indexedDBProvider.write<BookmarkDBRecord>(this.STORAGE_KEY, sku, { sku, imageData, dateAdded: new Date() });
  }

  async get() {
    if (this.dbState() !== 'open') {
      await this.openDB();
    }

    if (this.openPromise) {
      await this.openPromise;
    }
    const bookmarks = await this.indexedDBProvider.load<BookmarkDBRecord>(this.STORAGE_KEY);
    bookmarks?.sort((a, b) => b.dateAdded?.getTime() - a.dateAdded?.getTime());
    return bookmarks;
  }

  async clear() {
    if (this.dbState() !== 'open') {
      await this.openDB();
    }

    if (this.openPromise) {
      await this.openPromise;
    }
    this.indexedDBProvider.clear(this.STORAGE_KEY);
  }
}
